// framework/common/schemas/collections/users.js
import { z } from "zod";

// This is the single source of truth for the User data model.
const UserSchema = z.object({
    _id: z.string().optional().describe("MongoDB ObjectId"),
    // --- Authentication & Tenancy ---
    email: z.string().email("Invalid email format.").describe("Globally unique email for the user."),
    password: z.string().optional().nullable().describe("User's hashed password. This is never sent to the client."),

    subscriptionId: z.string().describe("The internal, unique ID of the subscription/tenant."),
    serviceCode: z.string().describe("The user-facing code for the subscription/tenant."),

    // --- User Identity & Role (within a subscription) ---
    username: z.string().min(3, "Username must be at least 3 characters long.").describe("Unique username within a subscription."),
    firstName: z.string().optional().nullable(),
    lastName: z.string().optional().nullable(),
    role: z.string().default("member").describe("The role assigned to the user within their subscription."),

    // --- Account Status ---
    isActive: z.boolean().default(true).describe("Indicates if the user account is active."),
    isVerified: z.boolean().default(false).describe("Indicates if the user's email has been verified."),

    phoneNumber: z.string().optional().nullable().describe("The user's primary phone number."),
    isPhoneVerified: z.boolean().default(false).describe("Indicates if the user's phone number has been verified."),

    // --- NEW: Onboarding Status ---
    onboardingCompleted: z.boolean().default(false).describe("Indicates if the user has completed the initial onboarding wizard."),

    // --- Profile Information (can be edited by user) ---
    profilePicture: z.string().optional().nullable().describe("Relative path to the user's profile picture file."),
    jobTitle: z.string().optional().nullable(),
    department: z.string().optional().nullable(),

    oauthProfiles: z.array(z.object({
        provider: z.string().describe("The name of the OAuth provider (e.g., 'google')."),
        id: z.string().describe("The unique user ID from the provider."),
    })).optional().default([]).describe("Stores linked third-party authentication profiles."),

    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
});

export default UserSchema;

export const UserSignupSchema = z.object({
    email: UserSchema.shape.email,
    password: z.string().min(8, "Password must be at least 8 characters."), 
    firstName: UserSchema.shape.firstName.optional(), 
    lastName: UserSchema.shape.lastName.optional(),   
    username: UserSchema.shape.username.optional(),   
    companyName: z.string().optional().describe("Used to help generate a serviceCode."),
    omnixContactId: z.string().optional().nullable().describe("The existing contactId from Omnix, if the signup originated there."),
});

// For an admin inviting/adding a user to an existing subscription
export const UserInviteSchema = z.object({
    email: UserSchema.shape.email,
    username: UserSchema.shape.username,
    role: UserSchema.shape.role,
    firstName: UserSchema.shape.firstName,
    lastName: UserSchema.shape.lastName,
});

// For a user updating their own profile information
export const UserProfileUpdateSchema = UserSchema.pick({
    firstName: true,
    lastName: true,
    jobTitle: true,
    department: true,
    // contactNumber is no longer a standard user field, it's app-specific
}).partial(); // All fields are optional during an update

// For an admin updating a user's information
export const AdminUserUpdateSchema = UserSchema.pick({
    username: true,
    firstName: true,
    lastName: true,
    role: true,
    isActive: true,
    jobTitle: true,
    department: true,
}).partial();

export const ChangePasswordSchema = z.object({
    currentPassword: z.string().min(1, "Current password is required."),
    newPassword: z.string().min(8, "New password must be at least 8 characters."),
});